import { Api } from '@/Api';
import { defineStore } from 'pinia';
import { useDebug, DEBUG_NAMESPACE } from '../../../composables/useDebug';

const { debug } = useDebug(DEBUG_NAMESPACE.STORE);

export interface Reaction {
  id?: string;
  name: string;
  count: number;
  highlight?: boolean;
}

export interface ReactionState {
  list: Reaction[];
}

export const useReactionsStore = defineStore('reactionsStore', {
  state: () =>
    ({
      list: [
        { name: 'thumbsup', count: 0, highlight: false },
        { name: 'thumbsdown', count: 0, highlight: false },
        { name: 'fire', count: 0, highlight: false },
        { name: 'heart', count: 0, highlight: false },
      ],
    }) as ReactionState,
  getters: {
    emojiList: state => state.list.map(l => l.name),
    reactions: state => state.list,
    isActiveReaction() {
      return (name: string) => {
        const reaction = this.getByName(name);

        if (!reaction) {
          return false;
        }

        return reaction.highlight;
      };
    },
    getByName: state => (name: string) => state.list.find(r => r.name === name),
    reactionsCount(state) {
      return (reaction: string) => {
        const reactionFound = state.list.find(r => r.name === reaction);
        return reactionFound ? reactionFound.count : 0;
      };
    },
  },
  actions: {
    async load(itemId: string) {
      try {
        const data = await Api.item.reactions.list(itemId);
        await data.forEach((res: any) => {
          const reactionIdx = this.reactions.findIndex(r => r.name === res.name);

          if (reactionIdx < 0) {
            return;
          }

          this.reactions[reactionIdx].id = res.id;
          this.reactions[reactionIdx].count = res.count;
        });
      } catch (error) {
        debug(error);
      }
    },
    async addReaction(name: string, id: string) {
      const reactionIdx = this.reactions.findIndex(r => r.name === name);

      try {
        const res = await Api.item.reactions.add(name, id);

        this.reactions[reactionIdx].count = res.count;
        this.reactions[reactionIdx].id = res.id;
      } catch (error) {
        this.reactions[reactionIdx].count++;
        debug(error);
      }

      this.reactions[reactionIdx].highlight = true;
    },
    async removeReaction(name: string) {
      const reactionIdx = this.reactions.findIndex(r => r.name === name);

      if (this.reactions[reactionIdx].count === 0) {
        return;
      }

      const reactionId = this.reactions[reactionIdx].id;

      if (reactionId) {
        try {
          const res = await Api.item.reactions.remove(reactionId);
          this.reactions[reactionIdx].count = res.count;
        } catch (error) {
          this.reactions[reactionIdx].count--;
          debug(error);
        }
      }

      this.reactions[reactionIdx].highlight = false;
    },
  },
});
